import { useCallback, useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";

export interface PaginatedData<T, TNext> {
  items: T[];
  hasMore: boolean;
  nextItem?: TNext | null;
}
export default function useInfiniteScroll<T, TNext>(
  initialData: PaginatedData<T, TNext>,
  fetcher: (
    nextItem: TNext,
    signal?: AbortSignal,
  ) => Promise<PaginatedData<T, TNext>>,
  key?: string,
) {
  const loading = useRef<boolean>(false);
  const [items, setItems] = useState(initialData.items);
  const [hasMore, setHasMore] = useState(initialData.hasMore);
  const [nextItem, setNextItem] = useState(initialData.nextItem);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const versionRef = useRef<number>(0);
  const keyRef = useRef<string | undefined>(key);

  useEffect(() => {
    setItems(initialData.items);
    setNextItem(initialData.nextItem);
    setHasMore(initialData.hasMore);
  }, [initialData]);

  // Cancel in-flight requests when key changes (e.g., filter changes)
  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      versionRef.current += 1;
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      controllerRef.current = new AbortController();
      loading.current = false;
    } else if (!controllerRef.current) {
      controllerRef.current = new AbortController();
    }
  }, [key]);

  // Function to load more items
  const loadMoreItems = useCallback(async () => {
    if (
      loading.current ||
      !hasMore ||
      nextItem === undefined ||
      nextItem === null
    ) {
      return;
    }

    loading.current = true;
    const myVersion = versionRef.current;
    const signal = controllerRef.current?.signal;
    try {
      const response = await fetcher(nextItem, signal);
      if (myVersion !== versionRef.current) {
        return;
      }
      setItems((prevItems) => [...prevItems, ...response.items]);
      setHasMore(response.hasMore);
      setNextItem(response.nextItem);
    } catch (_err) {
      // Ignore fetch errors (including aborts)
    } finally {
      loading.current = false;
    }
  }, [loading, hasMore, nextItem, fetcher]);

  // IntersectionObserver to trigger load more items
  useEffect(() => {
    if (!hasMore) return;
    const currentSentinel = sentinelRef.current;

    if (currentSentinel) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadMoreItems();
        }
      });

      observer.observe(currentSentinel);
      return () => {
        observer.disconnect();
      };
    }
  }, [loadMoreItems, hasMore, nextItem, initialData]);

  return { items, loading, hasMore, sentinelRef, nextItem, setItems };
}
